import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Activity, AlertTriangle, RefreshCw, Trash2 } from "lucide-react";
import { readPerfLog, clearPerfLog, type PerfEntry } from "@/lib/perfLog";

const SLOW_MS = 1500;

const fmtTime = (ts: number) =>
  new Date(ts).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });

const PerfMetricsPanel = () => {
  const [entries, setEntries] = useState<PerfEntry[]>(() => readPerfLog());

  const refresh = () => setEntries(readPerfLog());

  useEffect(() => {
    const id = window.setInterval(refresh, 5000);
    return () => window.clearInterval(id);
  }, []);

  const { byName, totals } = useMemo(() => {
    const map = new Map<string, { name: string; scope: string; count: number; ok: number; failed: number; timeouts: number; max: number; sum: number; lastTs: number; lastError?: string; }>();
    let total = 0;
    let failed = 0;
    let timeouts = 0;
    let slow = 0;
    entries.forEach((e) => {
      total += 1;
      if (e.status === "timeout") timeouts += 1;
      if (!e.ok) failed += 1;
      if (e.durationMs >= SLOW_MS) slow += 1;
      const key = `${e.scope}::${e.name}`;
      const cur = map.get(key);
      if (cur) {
        cur.count += 1;
        cur.ok += e.ok ? 1 : 0;
        cur.failed += e.ok ? 0 : 1;
        cur.timeouts += e.status === "timeout" ? 1 : 0;
        cur.max = Math.max(cur.max, e.durationMs);
        cur.sum += e.durationMs;
        if (e.ts > cur.lastTs) {
          cur.lastTs = e.ts;
          cur.lastError = e.error;
        }
      } else {
        map.set(key, {
          name: e.name,
          scope: e.scope,
          count: 1,
          ok: e.ok ? 1 : 0,
          failed: e.ok ? 0 : 1,
          timeouts: e.status === "timeout" ? 1 : 0,
          max: e.durationMs,
          sum: e.durationMs,
          lastTs: e.ts,
          lastError: e.error,
        });
      }
    });
    const sorted = Array.from(map.values()).sort((a, b) => b.max - a.max);
    return { byName: sorted, totals: { total, failed, timeouts, slow } };
  }, [entries]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Activity className="w-4 h-4" />
          Client Performance Metrics
        </CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={refresh}>
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { clearPerfLog(); refresh(); }}>
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Stat label="Tracked requests" value={totals.total} />
          <Stat label="Slow (≥1.5s)" value={totals.slow} tone={totals.slow > 0 ? "warn" : "ok"} />
          <Stat label="Failed" value={totals.failed} tone={totals.failed > 0 ? "bad" : "ok"} />
          <Stat label="Timeouts" value={totals.timeouts} tone={totals.timeouts > 0 ? "bad" : "ok"} />
        </div>

        {byName.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No client metrics captured yet. Open the public Conference page in another tab to populate this panel.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3">Request</th>
                  <th className="py-2 pr-3">Calls</th>
                  <th className="py-2 pr-3">Avg</th>
                  <th className="py-2 pr-3">Max</th>
                  <th className="py-2 pr-3">Failures</th>
                  <th className="py-2 pr-3">Last</th>
                </tr>
              </thead>
              <tbody>
                {byName.map((row) => {
                  const avg = Math.round(row.sum / row.count);
                  const tone = row.timeouts > 0 || row.failed > 0
                    ? "bad"
                    : row.max >= SLOW_MS
                      ? "warn"
                      : "ok";
                  return (
                    <tr key={`${row.scope}-${row.name}`} className="border-t">
                      <td className="py-2 pr-3">
                        <div className="font-medium">{row.name}</div>
                        <div className="text-[10px] text-muted-foreground">{row.scope}</div>
                        {row.lastError && (
                          <div className="text-[10px] text-red-600 flex items-center gap-1 mt-0.5">
                            <AlertTriangle className="w-3 h-3" /> {row.lastError}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-3">{row.count}</td>
                      <td className="py-2 pr-3">{avg}ms</td>
                      <td className="py-2 pr-3">
                        <Badge variant="outline" className={
                          tone === "bad" ? "border-red-300 text-red-700"
                          : tone === "warn" ? "border-amber-300 text-amber-700"
                          : "border-emerald-300 text-emerald-700"
                        }>
                          {row.max}ms
                        </Badge>
                      </td>
                      <td className="py-2 pr-3">
                        {row.failed > 0 || row.timeouts > 0
                          ? `${row.failed} err${row.timeouts ? ` / ${row.timeouts} timeouts` : ""}`
                          : "—"}
                      </td>
                      <td className="py-2 pr-3 text-muted-foreground">{fmtTime(row.lastTs)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[11px] text-muted-foreground">
          Captured locally in this browser. Open the public Conference page (or have a visitor do so) to record more samples.
        </p>
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, tone = "ok" }: { label: string; value: number; tone?: "ok" | "warn" | "bad" }) => (
  <div className={
    "rounded-md border px-3 py-2 " +
    (tone === "bad" ? "bg-red-50 border-red-200"
     : tone === "warn" ? "bg-amber-50 border-amber-200"
     : "bg-secondary/40 border-border")
  }>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-lg font-semibold">{value}</div>
  </div>
);

export default PerfMetricsPanel;
